/**
 * Error codes that uniquely identify an error within the application.
 * They are grouped in a module/submodule fashion and they are incremental,
 * every module/submodule has up to 1000 error codes and the first error for each
 * module/submodule must have an error code multiple of 1000. An underscore
 * for each error code as a thousand separator.
 */
export enum ErrorCodes {
	E2E_REQUEST_FAILED = 'E2E_REQUEST_FAILED',

	MISC_INTERNAL_SERVER_ERROR = '0_000',
	MISC_NOT_FOUND = '0_001',
	MISC_INVALID_WEBHOOK_SIGNATURE = '0_002',
	MISC_NOT_IMPLEMENTED = '0_003',
	MISC_INVALID_RANGE_HEADER = '0_004',
	MISC_RANGE_OUT_OF_LIMITS = '0_005',
	MISC_UNKNOWN_ERROR = '0_006',
	MISC_BLOCKED_ERROR = '0_007',

	MIDDLEWARE_MISSING_AUTH_TOKEN = '1_000',
	MIDDLEWARE_PASSWORD_ALREADY_CHANGED = '1_001',
	MIDDLEWARE_MINIMUM_CLIENT = '1_002',
	MIDDLEWARE_INVALID_VERSION_FORMAT = '1_003',
	MIDDLEWARE_REQUESTS_NOT_ALLOWED = '1_004',
	MIDDLEWARE_TOO_MANY_REQUESTS = '1_005',
	MIDDLEWARE_INVALID_REQUEST_ORIGIN = '1_006',
	MIDDLEWARE_JSON_PAYLOAD_TOO_LARGE = '1_007',

	COMMON_VALIDATE_USER_NOT_FOUND = '2_000',
	COMMON_VALIDATE_WRONG_USER_TYPE = '2_001',
	COMMON_VALIDATE_USER_ADMIN_PERMISSIONS = '2_002',
	COMMON_VALIDATE_USER_EMAIL_NOT_VERIFIED = '2_003',

	ACHIEVEMENTS_COULD_NOT_CREATE = '3_000',

	LOGGER_LOG_LEVEL_NOT_FOUND = '4_000',

	AUTH_BAD_GOOGLE_PAYLOAD = '5_000',
	AUTH_FIELD_CONFLICT = '5_001',
	AUTH_INCORRECT_PASSWORD = '5_002',
	AUTH_EMAIL_ALREADY_VERIFIED = '5_003',
	AUTH_EMAIL_RECENTLY_SENT = '5_004',
	AUTH_INVALID_ACTION_TYPE = '5_005',
	AUTH_INVALID_REGISTER_METHOD = '5_006',
	AUTH_BAD_APPLE_PAYLOAD = '5_007',
	AUTH_USER_ONLY_SOCIAL = '5_008',
	AUTH_REQUIRES_AUTHENTICATION = '5_009',
	/** @deprecated */
	AUTH_INTEREST_LENGTH_EXCEEDED = '5_010',
	AUTH_AT_LEAST_ONE_BEST_DESCRIBE = '5_011',
	AUTH_REQUIRES_SPECIFIC_USER_FEATURE_FLAG = '5_012',
	AUTH_REQUIRES_SPECIFIC_ADMIN_TOOLS_FEATURE_FLAG = '5_013',
	AUTH_REQUIRES_SPECIFIC_ADMIN_DASHBOARD_FEATURE_FLAG = '5_014',
	AUTH_REQUIRES_SPECIFIC_PERMISSIONS = '5_015',
	AUTH_OAUTH_ERROR = '5_016',
	AUTH_OAUTH_QUERY_USER_DOES_NOT_MATCH_AUTHED_USER = '5_017',
	USERNAME_REQUIRED = '5_018',
	AUTH_OAUTH_INVALID_REDIRECT_URI = '5_019',
	AUTH_OAUTH_INVALID_CLIENT_METADATA = '5_020',

	COMMENTS_NOT_FOUND = '6_000',
	COMMENTS_NOT_BELONGING = '6_001',

	CUSTOM_PROPERTIES_INVALID_OPTIONS_ARRAYS = '7_000',
	CUSTOM_PROPERTIES_INVALID_OPTION_SINGLE = '7_001',
	CUSTOM_PROPERTIES_INVALID_PROPERTY_NAME = '7_002',
	CUSTOM_PROPERTIES_INVALID_TYPE = '7_003',
	CUSTOM_PROPERTIES_ASSERTION_FAILED = '7_004',
	CUSTOM_PROPERTIES_NONEXISTENT_TYPE = '7_005',
	CUSTOM_PROPERTIES_NAME_AND_TYPE_CONFLICT = '7_006',
	CUSTOM_PROPERTIES_NO_OPTIONS = '7_007',
	CUSTOM_PROPERTIES_NO_UNIT = '7_0O8',
	CUSTOM_PROPERTIES_NO_TYPE = '7_009',
	CUSTOM_PROPERTIES_NOT_FOUND = '7_010',
	CUSTOM_PROPERTIES_NOT_FOUND_FOR_USER = '7_011',
	CUSTOM_PROPERTY_ALREADY_IN_NODE = '7_012',
	CUSTOM_PROPERTY_NOT_IN_NODE = '7_013',
	CUSTOM_PROPERTY_DOES_NOT_HAVE_ADD_EDIT_REMOVE_FROM_NODE_PERMISSION = '7_014',
	CUSTOM_PROPERTY_DOES_NOT_HAVE_REMOVE_FROM_USER_PERMISSION = '7_015',
	CUSTOM_PROPERTIES_NONEXISTENT_DATATYPE = '7_016',
	CUSTOM_PROPERTY_ALREADY_EXISTS = '7_017',
	CUSTOM_PROPERTY_IS_NOT_FROM_REACH = '7_018',
	CUSTOM_PROPERTIES_NOT_USER_DEFINED_OPTIONS = '7_019',
	CUSTOM_PROPERTY_PROCCESS_TYPE_NOT_EXPECTED_VALUE = '7_020',
	CUSTOM_PROPERTY_INVALID_FOR_MERGE = '7_021',
	CUSTOM_PROPERTIES_INVALID_TYPE_FOR_MERGE = '7_0022',
	CUSTOM_PROPERTIES_INVALID_AI_POPULATED_PROPERTY = '7_023',
	CUSTOM_PROPERTIES_AI_POPULATED_PROPERTY_REQUIRES_DESCRIPTION = '7_024',
	CUSTOM_PROPERTY_CANNOT_MODIFY_DUE_TO_REACH_PROPERTY = '7_025',

	EDGES_IS_RECURSIVE = '8_000',

	TAKEOUT_COOLDOWN = '9_000',

	EXPORT_WRONG_VERSION = '10_000',

	FILES_COULD_NOT_BE_PARSED = '11_000',
	FILES_CREATED_NOT_FOUND = '11_001',
	FILES_NOT_FOUND_FROM_URL = '11_002',
	FILES_COULD_NOT_CREATE_SINGLE = '11_003',
	FILES_COULD_NOT_CREATE_MULTIPLE = '11_004',
	FILES_COULD_NOT_FIND = '11_005',
	FILES_CANNOT_TRANSCRIBE_NON_MEDIA_FILE = '11_006',
	FILES_TRANSCRIPTION_ALREADY_REQUESTED = '11_007',
	FILES_TRANSCRIPTION_IS_NOT_READY = '11_008',
	FILES_UPLOADED_EMPTY_FOLDER = '11_009',
	FILES_MAX_FILE_SIZE_EXCEEDED = '11_010',
	FILES_MAX_BUNDLE_SIZE_EXCEEDED = '11_011',
	FILES_SOURCE_NOT_IMPORTABLE = '11_012',
	FILES_TOO_MANY_FILES_TO_ABSORB = '11_013',

	/** @deprecated */
	IMPORT_NO_PERMISSIONS = '12_000',
	IMPORT_UNEXPECTED_NODE_TYPE = '12_001',
	IMPORT_INVALID_FORMAT = '12_002',
	IMPORT_ID_TO_PARSE_NOT_FOUND = '12_003',

	MULTER_FILE_TOO_LARGE = '13_000',
	MULTER_FILE_UPLOAD_FAILED = '13_001',
	MULTER_NO_FILE_PROVIDED = '13_002',
	MULTER_WRONG_MIMETYPE = '13_003',
	MULTER_TOO_MANY_ITEMS = '13_004',
	MULTER_FILES_CONTAINS_A_VIRUS = '13_005',
	MULTER_FILE_SCAN_FAILED = '13_006',
	MULTER_FILE_TOO_LARGE_TO_SCAN = '13_007',

	NODE_DOES_NOT_CONTAIN_PICTURE = '14_000',

	NODES_PROPERTY_NOT_DEFINED = '15_000',
	NODES_TYPE_NOT_SUPPORTED = '15_001',
	NODES_INVALID_CLIENT_SOURCE_OR_CREATION_SOURCE = '15_002',
	WRONG_LENGTH_ID_ARRAY = '15_003',
	NODES_CONTENT_IS_EMPTY = '15_004',
	NODES_CONTENT_IS_LOADING = '15_005',
	NODES_CHUNKS_CANNOT_EDIT_IF_NOT_LEARNED = '15_006',
	NODES_CHUNK_NOT_FOUND = '15_007',
	NODES_CHUNKS_IS_TOO_LONG = '15_008',
	NODES_NODE_CATEGORIES_DO_NOT_EXIST = '15_009',

	SEARCH_TYPE_SHOULD_NOT_HAVE_BEEN_FOUND = '16_000',
	SEARCH_OPERATOR_FORBIDDEN_FOR_TYPE = '16_001',
	SEARCH_OPERATOR_FORBIDDEN_FOR_ATTRIBUTE = '16_002',
	SEARCH_OPERATOR_INVALID = '16_003',
	SEARCH_SEARCH_PARAMS_REQUIRED = '16_004',
	SEARCH_OPERATOR_VALUE_REQUIRED = '16_005',
	PROPERTY_FILTER_VALUE_ASSERTION_FAILED = '16_006',
	NODE_FILTER_NOT_IN_WORKSPACE = '16_007',

	ACCESS_EXISTENCE_NO_ACCESS = '17_000',
	ACCESS_EXISTENCE_NODE_WRONG_WORKSPACE = '17_001',
	ACCESS_EXISTENCE_NODE_WRONG_TYPE = '17_002',
	ACCESS_EXISTENCE_NODE_INSIDE_EXCLUDE = '17_003',
	ACCESS_EXISTENCE_NO_ACCESS_TO_EDGE = '17_004',
	ACCESS_EXISTENCE_USER_NOT_FOUND = '17_005',

	ELASTIC_SEARCH_INVALID_ARGUMENTS = '18_000',
	ELASTIC_SEARCH_UPDATE_FAILED = '18_001',
	ELASTIC_SEARCH_NO_RESULT = '18_002',
	ELASTIC_SEARCH_COULD_NOT_MAP_TYPE_TO_INDEX = '18_003',
	ELASTIC_SEARCH_DOCUMENT_CREATION_FAILED = '18_004',
	ELASTIC_SEARCH_MULTIPLE_DOCUMENT_CREATION_FAILED = '18_005',
	ELASTIC_SEARCH_MULTIPLE_DOCUMENT_UPDATE_FAILED = '18_006',
	ELASTIC_SEARCH_COULD_NOT_PARSE_INDEX_NAME = '18_007',

	FILE_ANALYSIS_INSTANCE_ALREADY_CREATED = '19_000',
	FILE_ANALYSIS_NO_URLS_PROVIDED = '19_001',
	FILE_ANALYSIS_TIMEOUT_EXCEEDED = '19_002',

	JWT_WRONG_USE_CASE = '20_000',
	JWT_TOKEN_EXPIRED = '20_001',
	JWT_TOKEN_NOT_FOUND = '20_002',
	JWT_ERROR = '20_003',
	JWT_WRONG_ORIGIN = '20_004',
	JWT_REVOKED = '20_005',

	LOCALS_UNDEFINED_LOCAL_PROPERTY = '21_000',

	GOTENBERG_PDF_CONVERSION_FAILED = '22_000',

	STORAGE_FILE_COULD_NOT_BE_FOUND = '23_000',
	STORAGE_FILE_COULD_NOT_BE_WRITTEN = '23_001',
	STORAGE_FILE_COULD_NOT_BE_COMPRESSED = '23_002',

	SPOTLIGHTS_NOT_FOUND = '24_000',
	SPOTLIGHTS_ALREADY_EXISTS = '24_001',
	SPOTLIGHTS_COULD_NOT_CREATE = '24_002',

	WORKSPACES_NOT_FOUND = '26_000',
	WORKSPACES_COULD_NOT_CREATE = '26_001',
	WORKSPACES_ALREADY_EXISTS = '26_002',
	WORKSPACES_COULD_NOT_DELETE_INSIDE_NODES = '26_003',
	WORKSPACES_CANNOT_DELETE_INTEGRATION_WORKSPACE = '26_004',
	WORKSPACES_MERGE_MINIMUM_SIZE = '26_005',
	WORKSPACES_LIMIT_EXCEEDED = '26_006',

	UTILS_OBJECT_TO_CLASS_TRANSFORMATION_FAILED = '27_000',
	UTILS_OBJECT_TO_CLASS_TRANSFORMATION_FAILED_WITH_ERRORS = '27_001',
	UTILS_UNEXPECTED_NULL_OR_UNDEFINED = '27_002',
	UTILS_UNIT_CANNOT_BE_CONVERTED = '27_003',
	UTILS_UNEXPECTED_UNREACHABLE = '27_004',
	UTILS_INVALID_JSON_FORMAT = '27_005',
	UTILS_COMPRESSION_FAILED = '27_006',
	UTILS_CANNOT_LOAD_MODULE = '27_007',

	DRAGONFLY_CONNECTION_FAILED = '28_000',
	DRAGONFLY_M_GET_META_KEYS_LENGTH_MISMATCH = '28_001',
	DRAGONFLY_LOCK_ACQUISITION_FAILED = '28_002',
	DRAGONFLY_PIPE_EXECUTION_FAILED = '28_003',
	DRAGONFLY_CHUNK_SIZE_MUST_BE_GREATER_THAN_ZERO = '28_004',

	QUOTAS_NOT_ENOUGH_NODE_SLOTS_AVAILABLE = '29_000',
	QUOTAS_NOT_ENOUGH_STORAGE_SLOTS_AVAILABLE = '29_001',
	QUOTAS_SLOT_INCREMENT_MUST_BE_POSITIVE_INTEGERS = '29_002',
	QUOTAS_SLOT_INCREMENT_HAS_INVALID_VALUE = '29_003',
	QUOTAS_SERVICE_NOT_INJECTED = '29_004',
	QUOTAS_NOT_ENOUGH_CHAT_KNOWLEDGE_CONVERSATIONS_SLOTS_AVAILABLE = '29_005',
	QUOTAS_NOT_ENOUGH_CHAT_KNOWLEDGE_MESSAGES_SLOTS_AVAILABLE = '29_006',
	QUOTAS_NOT_ENOUGH_CHAT_WIDGETS_SLOTS_AVAILABLE = '29_007',
	QUOTAS_NOT_ENOUGH_CHAT_WIDGET_MESSAGES_SLOTS_AVAILABLE = '29_008',
	QUOTAS_NOT_ENOUGH_INTEGRATION_NODE_SLOTS_AVAILABLE = '29_009',
	QUOTAS_SLOT_NOT_EDITABLE = '29_010',

	SETTINGS_NO_DATA = '31_000',

	TABLE_VIEWS_NOT_FOUND = '32_000',
	TABLE_VIEWS_COLUMNS_NOT_FOUND = '32_001',

	USERS_WITH_PRIVATE_ID_NOT_FOUND = '34_000',
	USER_NOT_FOUND_BY_QUERY = '34_001',
	INVALID_SUMMARY_INFO_TYPE = '34_002',
	USER_EMAIL_RECENTLY_SENT = '34_003',
	USER_LIST_SORT_BY_TYPE_NOT_IMPLEMENTED = '34_004',

	APPLE_AUTH_VERIFICATION_FAILED = '35_000',
	APPLE_AUTH_CANNOT_FIND_KEY = '35_001',
	APPLE_AUTH_TOKEN_EXPIRED = '35_002',
	APPLE_AUTH_NONCE_DID_NOT_MATCH = '35_003',

	UNSPlASH_TOO_MANY_REQUESTS = '36_000',
	UNSPlASH_FEATURE_NOT_GRANTED = '36_001',

	WEBSITES_COULD_NOT_PERFORM_ANALYSIS = '37_000',
	WEBSITES_COULD_NOT_PARSE_URL = '37_001',
	WEBSITES_NOT_FOUND_BY_URL = '37_002',
	WEBSITES_NOT_FOUND = '37_003',
	WEBSITES_MUST_HAVE_URL = '37_004',
	WEBSITES_MUST_HAVE_URL_OR_TITLE = '37_005',
	WEBSITES_CANNOT_IMPORT_BOOKMARKS_COOLDOWN = '37_006',

	MILVUS_QUERY_FAILED = '38_000',
	MILVUS_SEARCH_FAILED = '38_001',

	WEBSITE_ANALYSIS_MISSING_CONTENT_TYPE = '39_000',
	WEBSITE_ANALYSIS_NOT_A_WEBSITE = '39_001',
	WEBSITE_ANALYSIS_FETCH_TIMEOUT = '39_002',
	WEBSITE_ANALYSIS_FETCH_FAILED = '39_003',
	WEBSITE_ANALYSIS_PARSE_FAILED = '39_004',
	WEBSITE_ANALYSIS_PARSE_TIMEOUT = '39_005',
	WEBSITE_ANALYSIS_READABILITY_TIMEOUT = '39_006',
	WEBSITE_ANALYSIS_DOM_SANITIZE_TIMEOUT = '39_007',
	WEBSITE_ANALYSIS_TIMEOUT_EXCEEDED = '39_008',
	WEBSITE_ANALYSIS_DOCUMENT_URL_EXTRACT_TIMEOUT = '39_009',

	EMAILS_MAILGUN_API_ERROR = '40_000',
	INSIGHT_NO_METADATA = '40_003',
	INSIGHT_NO_CONFIG = '40_004',
	EMAIL_CANNOT_BE_SENT = '40_005',
	CANNOT_COMPLETE_USER_REGISTRATION = '40_006',

	JOBS_COULD_NOT_CHANGE_JOB_RUN_STATUS = '41_000',
	JOBS_COULD_NOT_FIND_LAST_JOB_BY_TYPE = '41_001',
	JOBS_JOB_TYPE_IS_NOT_NODE_JOB = '41_002',
	JOBS_STATUS_IS_NOT_PENDING = '41_003',

	NOTES_COULD_NOT_FIND_ATTACHMENT = '43_000',
	NOTES_IMPORT_COULD_NOT_PARSE = '43_001',
	NOTES_INVALID_IMPORT_FORMAT = '43_002',
	NOTES_COULD_NOT_CREATE = '43_003',

	WIKIMEDIA_RESPONSE_ERROR = '44_000',

	UPDATE_LOGS_COULD_NOT_CREATE = '45_000',
	UPDATE_LOGS_COULD_NOT_BE_FOUND = '45_001',
	UPDATE_LOGS_IMAGE_INDEX_TO_LONG = '45_002',
	UPDATE_LOGS_INVALID_JSON = '45_003',

	STATISTICS_ID_NOT_FOUND = '46_000',

	NOTE_TEMPLATES_CANNOT_DELETE_FOREIGN_TEMPLATE = '47_000',

	AVATAR_USER_NOT_IN_TEAM = '48_000',

	//#region SharePoint
	SHAREPOINT_INTEGRATION_HASH_INCONSISTENCY = '49_000',
	SHAREPOINT_INTEGRATION_NO_NEXT_DELTA_TOKEN = '49_001',
	SHAREPOINT_INTEGRATION_REAUTHENTICATION_INVALID_TOKEN = '49_002',
	SHAREPOINT_INTEGRATION_REAUTHENTICATION_USER_MISMATCH = '49_003',
	//#endregion

	INTEGRATIONS_NOT_FOUND = '50_000',
	INTEGRATIONS_CANNOT_BE_DELETED = '50_001',
	INTEGRATIONS_UNKNOWN_TYPE = '50_002',
	INTEGRATIONS_CANNOT_EXECUTE_MANUALLY = '50_003',
	INTEGRATIONS_INVALID_HMAC = '50_004',
	INTEGRATIONS_OTHER_USER = '50_005',
	INTEGRATIONS_TYPE_ALREADY_CONTAINS_SYNCED = '50_006',
	INTEGRATIONS_NON_SYNCABLE = '50_007',
	INTEGRATIONS_NO_ITEMS_TO_SYNC = '50_008',
	INTEGRATIONS_USER_NOT_IN_ORGANIZATION = '50_009',

	REACH_VISION_REQUEST_FAILED = '51_000',

	NODE_CLEANUP_TYPE_NOT_MERGEABLE = '52_000',
	NODE_CLEANUP_TYPE_CANNOT_BE_CLEANED_UP = '52_001',

	UPTIME_ROBOT_RATE_LIMIT_EXCEEDED = '53_000',
	UPTIME_ROBOT_MONITOR_ID_NOT_FOUND = '53_001',

	ALBUM_FILE_DOES_NOT_BELONG = '54_000',
	ALBUM_FILE_IS_NOT_IMAGE_OR_VIDEO = '54_001',

	ACHIEVEMENTS_ALREADY_CLAIMED = '55_000',
	ACHIEVEMENTS_NOT_READY_TO_CLAIM = '55_001',

	LEVELING_ALREADY_CLAIMED = '56_000',
	LEVELING_NOT_READY_TO_CLAIM = '56_001',

	CUSTOMERIO_ERROR = '57_000',

	PLANS_USER_IS_NOT_A_TRIAL = '58_000',
	PLANS_EXPIRY_DATE_CANNOT_BE_IN_THE_PAST = '58_001',
	PLANS_NEW_EXPIRY_DATE_MUST_BE_AFTER_CURRENT_EXPIRY_DATE = '58_002',
	PLANS_USER_CANNOT_SWITCH_TO_SAME_PLAN = '58_003',
	PLANS_USER_NEEDS_TO_BE_ALPHA_FOR_PLAN_EXTENSION = '58_004',
	PLANS_LICENSE_IS_NOT_VALID = '58_005',
	PLANS_LICENSE_COULD_NOT_BE_ACTIVATED = '58_006',
	PLANS_USER_HAS_NO_ACTIVE_SUBSCRIPTION = '58_007',

	PIPELINES_COULD_NOT_CHANGE_PIPELINE_STATUS = '59_000',
	PIPELINES_COULD_NOT_FIND_LAST_PIPELINE_BY_TYPE = '59_001',
	PIPELINES_UNEXPECTED_PIPELINE_TYPE = '59_002',

	// DISCOURSE WAS 60_000: FOR CONVENTION, DO NOT REUSE THIS IDENTIFIER

	// ENIGMA WAS 61_000: FOR CONVENTION, DO NOT REUSE THIS IDENTIFIER

	REACH_TRANSCRIBER_UPLOAD_FAILED = '62_000',
	REACH_TRANSCRIBER_JOB_ALREADY_FINISHED = '62_001',
	REACH_TRANSCRIBER_INVALID_WEBHOOK_SIGNATURE = '62_002',
	REACH_TRANSCRIBER_TRANSCRIPTION_TIMED_OUT = '62_003',
	REACH_TRANSCRIBER_TRANSCRIPTION_FAILED = '62_004',

	MAPS_PINS_LIST_REQUEST_FAILED = '64_000',

	TICKET_ALREADY_RESOLVED = '65_000',
	SELF_IMPERSONATION = '65_001',

	TICKET_HAS_ACCESSES = '65_002',

	AZURE_INVALID_TOKEN = '66_000',
	AZURE_REAUTHENTICATION_REQUIRED = '66_001',

	// EVERNOTE WAS 67_000: FOR CONVENTION, DO NOT REUSE THIS IDENTIFIER

	NODE_UNPROCESSABLE_EMPTY_CONTENT = '68_000',
	NODE_LEARN_LOCK_ACQUISITION_TIMEOUT = '68_001',

	FILE_THUMBNAIL_TIMEOUT_EXCEEDED = '69_000',

	CHAT_KNOWLEDGE_CHAT_NOT_FOUND = '70_000',
	CHAT_KNOWLEDGE_MESSAGES_NOT_FOUND = '70_001',
	CHAT_KNOWLEDGE_NO_UPDATE_DATA_PROVIDED = '70_002',
	CHAT_KNOWLEDGE_TOOL_NAME_CONFLICT = '70_003',
	CHAT_KNOWLEDGE_TOOL_NOT_FOUND = '70_004',
	CHAT_KNOWLEDGE_CUSTOM_PROMPT_NOT_FOUND = '70_005',
	CHAT_KNOWLEDGE_CUSTOM_PROMPT_LIMIT_REACHED = '70_006',
	CHAT_KNOWLEDGE_INVALID_MCP_IDS = '70_007',

	GOOGLE_AUTH_ACCESS_TOKEN_EXPIRED = '71_000',
	GOOGLE_AUTH_REFRESH_TOKEN_EXPIRED = '71_001',
	GOOGLE_AUTH_MUST_AUTHORIZE_FIRST = '71_002',
	GOOGLE_DRIVE_RATE_LIMIT_SURPASSED = '71_003',
	GOOGLE_DRIVE_FILE_TOO_BIG_FOR_EXPORT = '71_004',
	GOOGLE_DRIVE_CLIENT_NOT_RETURNING_FILE = '71_005',
	GOOGLE_DRIVE_NOT_SYNCED = '71_006',
	GOOGLE_DRIVE_EXECUTION_IN_PROGRESS = '71_007',
	GOOGLE_DRIVE_SYNC_COOLDOWN = '71_008',
	GOOGLE_DRIVE_SYNC_IN_PROGRESS = '71_009',
	GOOGLE_DRIVE_NOT_EXECUTED = '71_010',
	GOOGLE_DRIVE_TOO_MANY_FILES_TO_UPDATE = '71_011',

	INTEGRATION_EXECUTION_NOT_FOUND = '72_000',
	INTEGRATION_EXECUTION_CANNOT_CREATE_INTEGRATION_EXECUTION_TYPE_COOLDOWN = '72_001',
	INTEGRATION_EXECUTION_CANNOT_CREATE_INTEGRATION_EXECUTION_UNTIL_LAST_FINISHES = '72_002',

	SUMMARY_REQUEST_FAILED = '73_000',

	TEAM_NAME_ALREADY_EXISTS = '74_000',
	TEAM_MEMBER_ALREADY_IN_ANOTHER_TEAM = '74_001',
	TEAM_DOESNT_EXIST = '74_002',
	TEAM_NOT_ENOUGH_MEMBERS = '74_003',
	TEAM_INCLUDES_NON_ALPHA_USERS = '74_004',

	MEDIA_PREVIEW_FILE_TYPE_NOT_ALLOWED = '75_000',
	MEDIA_PREVIEW_ONLY_ALBUM_ITEMS_OF_TYPE_VIDEO_ALLOWED = '75_001',
	MEDIA_PREVIEW_CONVERSION_TIMED_OUT = '75_002',
	MEDIA_PREVIEW_CONVERSION_FAILED = '75_003',

	CHAT_WIDGET_UNAUTHORIZED_SESSION = '76_000',
	CHAT_WIDGET_NOT_ACTIVE = '76_001',
	CHAT_WIDGET_MESSAGES_NOT_FOUND = '76_002',
	CHAT_WIDGET_NO_UPDATE_DATA_PROVIDED = '76_003',
	CHAT_WIDGET_INVALID_SEARCH = '76_004',
	CHAT_WIDGET_NOT_FOUND = '76_005',
	CHAT_WIDGET_CANNOT_SERVE_NODE_RESOURCE = '76_006',
	CHAT_WIDGET_SESSION_NOT_FOUND = '76_007',
	CHAT_WIDGET_FAILED_SEARCH = '76_008',
	CHAT_WIDGET_TOKEN_VERIFY_ERROR = '76_009',
	CHAT_WIDGET_CATEGORY_NOT_FOUND = '76_010',
	CHAT_WIDGET_MESSAGE_HAS_NO_PAYLOAD = '76_011',
	CHAT_WIDGET_EMAIL_NOTIFICATIONS_ARE_DISABLED = '76_012',
	CHAT_WIDGET_MICROSOFT_TENANT_ID_NOT_SET = '76_013',
	CHAT_WIDGET_SHAREPOINT_TOKEN_MISSING_ERROR = '76_014',
	CHAT_WIDGET_SHAREPOINT_TOKEN_VERIFY_ERROR = '76_015',
	CHAT_WIDGET_MICROSOFT_AUTH_VERIFY_ERROR = '76_016',
	CHAT_WIDGET_MICROSOFT_AUTH_MISSING_ERROR = '76_017',

	CHAT_WIDGET_CATEGORY_NOT_FOUND_FOR_EMAIL_SUBSCRIPTION = '76_018',
	CHAT_WIDGET_INCORRECT_PASSWORD = '76_019',
	CHAT_WIDGET_PASSWORD_NEEDED = '76_020',

	CHAT_WIDGET_URL_NOT_WHITELISTED = '76_021',

	CHAT_WIDGET_CUSTOM_PROMPT_NOT_FOUND = '76_022',

	CHAT_WIDGET_INTEGRATION_NOT_FOUND_FROM_MICROSOFT_TEAMS_TOKEN = '76_023',
	CHAT_WIDGET_NOT_FOUND_FROM_MICROSOFT_TEAMS_TOKEN = '76_024',

	CHAT_WIDGET_REPORT_ID_REQUIRED = '76_025',
	CHAT_WIDGET_REPORT_DOES_NOT_BELONG_TO_WIDGET = '76_026',
	CHAT_WIDGET_REPORT_SCHEDULE_DOES_NOT_BELONG_TO_WIDGET = '76_027',
	CHAT_WIDGET_REPORT_NO_UPDATE_DATA_PROVIDED = '76_028',
	CHAT_WIDGET_REPORT_LIMIT_REACHED = '76_029',
	CHAT_WIDGET_CONVERSATION_NOT_FOUND = '76_030',
	CHAT_WIDGET_SEARCH_PRESETS_LIMIT_REACHED = '76_031',
	CHAT_WIDGET_SEARCH_PRESET_NOT_FOUND = '76_032',
	CHAT_WIDGET_MICROSOFT_ENTRA_GROUP_NOT_ALLOWED = '76_033',
	CHAT_WIDGET_INVALID_ENTRA_GROUP_SETTINGS = '76_034',
	CHAT_WIDGET_ENTRA_GROUP_CHECK_FAILED = '76_035',
	CHAT_WIDGET_ATTACHMENT_NOT_FOUND = '76_036',
	CHAT_WIDGET_ATTACHMENT_EXTRACTION_FAILED = '76_037',
	CHAT_WIDGET_QUESTION_OR_ATTACHMENT_REQUIRED = '76_038',
	CHAT_WIDGET_ATTACHMENTS_LIMIT_REACHED = '76_039',
	CHAT_WIDGET_ATTACHMENTS_DISABLED = '76_040',

	CHAT_FAILED_SEARCH = '77_001',
	CHAT_MODEL_NOT_AVAILABLE = '77_002',
	CHAT_CUSTOM_PROMPT_NOT_FOUND = '77_003',
	CHAT_MESSAGE_PART_NOT_FOUND = '77_004',
	CHAT_ASK_UNKNOWN_ERROR = '77_005',
	CHAT_RETRIEVAL_UNAVAILABLE_ERROR = '77_006',

	CHAT_INTEGRATION_ALREADY_EXISTS = '78_000',
	CHAT_INTEGRATION_NOT_FOUND = '78_001',
	CHAT_INTEGRATION_INVALID_BODY = '78_002',

	LLM_TOOLS_NOT_PROVIDED = '79_000',
	LLM_TOOL_NOT_FOUND = '79_001',
	LLM_COMPLETION_ERROR = '79_002',
	LLM_MESSAGE_ROLE_NOT_SUPPORTED = '79_003',
	LLM_LENGTH_ERROR = '79_004',
	LLM_CONTENT_FILTERED_ERROR = '79_005',
	LLM_REFUSED_ERROR = '79_006',
	LLM_TOOL_RUN_ERROR = '79_007',
	LLM_TOOLS_RUN_ARGUMENTS_NOT_PROVIDED = '79_008',
	LLM_DOES_NOT_SUPPORT_TOOL_AND_SCHEMA = '79_009',
	LLM_RATE_LIMIT_ERROR = '79_010',
	LLM_PROVIDER_UNAVAILABLE_ERROR = '79_011',
	LLM_AUTHENTICATION_ERROR = '79_012',
	LLM_CONTEXT_LENGTH_EXCEEDED_ERROR = '79_013',
	LLM_EMPTY_RESPONSE_ERROR = '79_014',
	LLM_NETWORK_ERROR = '79_015',
	TEXT_EMBEDD_ERROR = '80_000',
	ENTITY_EXTRACTOR_SOURCES_NOT_FOUND = '80_001',
	DOCUMENT_RETRIEVER_SOURCE_NOT_FOUND = '80_002',
	DOCUMENT_EXTRACTION_SUBMISSION_FAILED = '81_002',
	DOCUMENT_EXTRACTION_EXTRACTION_FAILED = '81_003',
	DOCUMENT_EXTRACTION_TIMED_OUT = '81_004',
	DOCUMENT_EXTRACTION_OUTPUT_READ_FAILED = '81_005',
	DOCUMENT_EXTRACTION_PASSWORD_PROTECTED = '81_006',

	SITEMAP_INTEGRATION_UNREACHABLE_SITEMAP = '82_000',
	SITEMAP_INTEGRATION_INVALID_SITEMAP = '82_001',
	SITEMAP_INTEGRATION_ALREADY_EXISTS = '82_002',

	CHAT_WIDGET_FLAG_NOT_FOUND = '83_000',
	CHAT_WIDGET_FLAGS_LIMIT_REACHED = '83_001',

	//#endregion
	//#region Taxonomies
	TAXONOMY_NO_UPDATE_DATA_PROVIDED = '84_000',
	TAXONOMY_NOT_FOUND = '84_001',
	TAXONOMY_DELETE_NOT_ALLOWED = '84_002',
	TAXONOMY_WORKSPACE_ALREADY_HAS_TAXONOMY = '84_003',
	TAXONOMY_NO_WORKSPACES_FOR_GENERATION = '84_004',
	TAXONOMY_CATEGORY_RULE_NOT_FOUND = '84_006',
	TAXONOMY_RULE_NOT_FOUND = '84_007',
	TAXONOMY_RULE_INVALID_PAYLOAD = '84_008',
	TAXONOMY_RULE_NOT_MUTABLE = '84_009',
	TAXONOMY_RULE_NOT_DRAFT = '84_010',

	CHAT_TAXONOMY_CHAT_NOT_FOUND = '85_000',
	CHAT_TAXONOMY_MESSAGES_NOT_FOUND = '85_001',
	//#endregion

	//#region Semantic Property Matcher
	SEMANTIC_PROPERTY_MATCHER_CANNOT_FIND_CUSTOM_PROPERTIES = '86_000',
	SEMANTIC_PROPERTY_MATCHER_CANNOT_FIND_MATCHER = '86_001',
	SEMANTIC_PROPERTY_MATCHER_HAS_NO_FILTER_COLUMNS = '86_002',
	SEMANTIC_PROPERTY_MATCHER_INVALID_FILTER_COLUMNS = '86_003',
	SEMANTIC_PROPERTY_MATCHER_RESULTS_ALREADY_CACHED = '86_004',
	SEMANTIC_PROPERTY_MATCHER_RESULTS_NOT_CACHED = '86_005',
	SEMANTIC_PROPERTY_MATCHER_MISSING_REQUIRED_FILTERS = '86_006',
	SEMANTIC_PROPERTY_MATCHER_CANNOT_FIND_WORKSPACES = '86_007',
	SEMANTIC_PROPERTY_MATCHER_CSV_HEADER_ROW_NOT_FOUND = '86_008',
	SEMANTIC_PROPERTY_MATCHER_TARGET_CUSTOM_PROPERTY_IS_NOT_MULTI_SELECT = '86_009',
	SEMANTIC_PROPERTY_MATCHER_INVALID_CSV_FILE = '86_010',
	SEMANTIC_PROPERTY_MATCHER_INVALID_CSV_COLUMNS = '86_011',
	//#endregion

	//#region Edge Types
	EDGE_TYPES_NOT_FOUND = '87_000',
	EDGE_TYPES_PERMISSION_VIOLATION = '87_001',
	EDGE_TYPES_LIMIT_EXCEEDED = '87_002',
	EDGE_TYPES_NOT_LINKED_OR_USER = '87_003',
	//#endregion

	//#region Node Categories
	NODE_CATEGORY_NOT_FOUND = '88_000',
	NODE_CATEGORY_NAME_ALREADY_EXISTS = '88_001',
	NODE_CATEGORY_LIMIT_EXCEEDED = '88_002',
	NODE_CATEGORY_CUSTOM_PROPERTIES_NOT_FOUND = '88_003',
	NODE_CATEGORY_CONTENT_STILL_LOADING = '88_004',
	NODE_CATEGORY_CONTENT_EMPTY = '88_005',
	NODE_CATEGORY_CANNOT_MODIFY_SYSTEM_CATEGORY = '88_006',
	NODE_CATEGORY_CANNOT_DELETE_SYSTEM_CATEGORY = '88_007',
	NODE_CATEGORY_DESCRIPTION_EMPTY = '88_008',
	//#endregion

	//#region Import CSV Integration
	IMPORT_CSV_INTEGRATION_INVALID_NODE_CATEGORIES = '89_000',
	//#endregion

	//#region MCP
	MCP_SESSION_NOT_FOUND = '90_000',
	MCP_UNABLE_TO_CREATE_NEW_SESSION = '90_001',
	MCP_SESSION_ID_REQUIRED = '90_002',
	//#endregion

	//#region User API Keys
	USER_API_KEYS_LLM_PROVIDER_API_KEY_ALREADY_EXISTS = '91_000',
	USER_API_KEYS_EMBEDDER_PROVIDER_API_KEY_ALREADY_EXISTS = '91_001',
	USER_API_KEYS_API_KEY_NOT_FOUND = '91_002',
	USER_API_KEYS_AI_NOT_VALID = '91_003',
	USER_API_KEYS_AI_UNSUPPORTED_MODELS = '91_004',
	USER_API_KEYS_AI_PROVIDER_TEST_FAILED = '91_005',
	//#endregion

	//#region Chat MCP
	CHAT_MCP_OAUTH_ERROR = '92_000',
	CHAT_MCP_OAUTH_SESSION_EXPIRED = '92_001',
	CHAT_MCP_ALREADY_EXISTS = '92_002',
	CHAT_MCP_INVALID_TOOLS = '92_003',
	CHAT_MCP_NOT_FOUND = '92_004',
	CHAT_MCP_CONNECTION_ERROR = '92_005',
}
